<template>
  <details class="gp-observation" :open="isOpen">
    <summary class="gp-observation__header" role="button" @click.prevent="handleOpen">
      <span class="-flex -gap -text--color-mute">
        <small>{{ $d(observation.madeAt, 'date') }}</small>
        <rui-icon v-if="observation.content" name="article" :title="$t('js.observation.has_content')" class="-text--medium" />
        <rui-icon v-if="hasPictures" name="image" :title="$t('js.observation.has_pictures')" class="-text--medium" />
        <rui-icon-text v-if="observation.commentsCount > 0" icon="chat" :title="$t('activerecord.attributes.observation.comments_count')" class="-text--medium">
          <small class="-text--small">{{ observation.commentsCount }}</small>
        </rui-icon-text>
      </span>
      <span class="gp-observation__header__main">
        <span class="gp-observation__header__main__title">
          {{ observation.title }}
        </span>

        <rui-toolbar v-if="editable || destroyable">
          <rui-button v-if="editable" icon="pencil" :title="$t('generic.edit')" @click="editObservation(observation)" />
          <rui-button v-if="destroyable"
                      icon="trash"
                      variant="destructive"
                      :title="$t('generic.destroy')"
                      @click="destroyObservation(observation)" />
        </rui-toolbar>
      </span>
    </summary>

    <div class="_container">
      <div class="gp-observation__content">
        <div v-if="observation.content" class="_writing">
          {{ observation.content }}
        </div>

        <rui-thumbnails v-if="hasPictures" :list="observation.pictures" @open="handleImageOpen" />
      </div>

      <div class="gp-observation__comments">
        <h3 class="-h5">
          {{ $t('js.observations.comments') }}
          <rui-button :title="$t('js.generic.reload')" icon="reload" text @click="refreshComments(observation.id)" />
        </h3>
        <gp-comments-list ref="commentsList"
                          :data-observation-id="observation.id"
                          :subject="observation"
                          class="-mbd" />
      </div>
    </div>
  </details>
</template>

<script>
import RuiButton from '@experiments-labs/rise_ui/src/components/Button/Button.vue'
import RuiThumbnails from '@experiments-labs/rise_ui/src/components/DataDisplay/Thumbnails/Thumbnails.vue'
import RuiIcon from '@experiments-labs/rise_ui/src/components/Icon/Icon.vue'
import RuiIconText from '@experiments-labs/rise_ui/src/components/IconText/IconText.vue'
import RuiToolbar from '@experiments-labs/rise_ui/src/components/Toolbar/Toolbar.vue'
import GpCommentsList from '../Comments/List.vue'

import Observation from '../../../classes/models/Observation'
import Toaster from '../../../tools/Toaster'

/**
 * Displays an observation
 *
 * @noPlayground
 */
export default {
  name: 'GpObservation',
  components: { GpCommentsList, RuiIconText, RuiButton, RuiThumbnails, RuiToolbar, RuiIcon },
  props: {
    /** Observation to display */
    observation: { required: true, type: Observation },
  },
  data () {
    return {
      isOpen: false,
      showImages: false,
    }
  },
  computed: {
    hasPictures () { return this.$props.observation.pictures && this.$props.observation.pictures.length > 0 },
    editable () { return this.$store.getters.account?.id === this.$props.observation.userId },
    destroyable () { return this.editable || this.$store.getters.isMapOwner },
  },
  methods: {
    handleImageOpen (url) {
      const list = this.$props.observation.pictures.map(links => links.source)

      this.$emitBus('show-image', {
        list,
        selected: url,
      })
    },
    editObservation () { this.$emitBus('show-observation-form', this.$props.observation) },
    destroyObservation () {
      this.$emitBus('ask-confirmation', {
        message: this.$t('js.observation.destroy_confirmation'),
        callback: () => {
          this.$store.dispatch('destroyObservation', this.$props.observation.id)
            .then(() => { Toaster.success(this.$t('js.observation.successfully_destroyed')) })
            .catch(() => { Toaster.error(this.$t('js.observation.failed_to_destroy')) })
        },
      })
    },
    handleOpen () {
      this.isOpen = !this.isOpen
      if (this.isOpen) this.$refs.commentsList.loadComments(true)
    },
    refreshComments () {
      this.$refs.commentsList.loadComments(true)
    },
  },
}
</script>
